/* eslint-disable max-len */
import { tint } from 'polished';
import React, { useCallback } from 'react';
import { alea } from 'seedrandom';
import styled from 'styled-components';
import { shadeSafe, useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ColorGroups, ColorList, Colors, Prompt, toRem } from '../../../ui';
import { KeepPopoutWidgetOpenButton } from '../../../ui/KeepOpenButton';
import { Position } from '../../../ui/Position';
import { useResolve } from '../../Container';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
// import { PaperHaug } from './PaperHaug';

const { ItemGroup, ItemSet, Item, ItemPreview } = ElementSelector;

const computeRotation = (color: string) => {
    const rand = alea(color, { global: true });
    return Math.floor(rand() * 3) - 1;
};

const StickyPreview = styled.div<{ color: string, large: boolean }>`
    width: ${p => toRem(p.large ? 5 : 3)};
    height: 3rem;
    border-radius: .125rem;
    box-shadow: 0px .0625rem .1875rem rgba(0, 0, 0, 0.1);
    background: ${p => tint(.66, p.color)};
    transform: rotate(${p => computeRotation(p.color)}deg);
`;

const IndexCardPreview = styled.div<{ color: string }>`
    width: 8rem;
    height: 6rem;
    border-radius: .125rem;
    box-shadow: 0px .0625rem .1875rem rgba(0, 0, 0, 0.2);
    background-color: white;
    background-image: ${p => `
        linear-gradient(180deg, white 1rem, ${tint(.5, p.color)} 1rem, ${tint(.5, p.color)} calc(1rem + .125rem), transparent .0625rem),
        repeating-linear-gradient(0deg, transparent, transparent 0.5rem, #DDD .0625rem, #DDD calc(0.5rem + .0625rem))
    `};
`;

const TaskCardFrame = styled.div<{ color: string }>`
    position: relative;
    width: 6rem;
    height: 4rem;
    border-radius: .125rem;
    border: solid .125rem ${p => p.color};
    border-left-width: .375rem;
    background: white;
    color: rgba(0,0,0,.5);
`;

const TaskCardPreview = (props: { color: string }) =>
    <TaskCardFrame {...props} />;

const RoadmapCardPreview = styled.div<{ color: string }>`
    width: 100%;
    height: 2rem;
    border-radius: .5rem;
    border: solid .125rem ${p => shadeSafe(.05, p.color)};
    background-color: ${p => p.color};
`;

const brandColors = [
    Colors.primary,
    Colors.negative,
    Colors.positive,
    Colors.mystery,
    Colors.observe,
    Colors.gray600,
];

const id = (...x: string[]) => x.join('/');

const stickyColors = ColorList.slice(0, 18).concat(['#000000', '#ffffff']);

const stickies = ItemGroup('stickies', 'Stickies', 'deqo://icons/d2/sticky@[0.8,0.8,0]', [
    ItemSet('stickies', 'Stickies', 6, stickyColors.map(x =>
        Item(id('stickies', x), null, { color: x, large: false, blend: .75 }, ItemPreview(StickyPreview))
    )),
    ItemSet('stickiesBig', 'Big Stickies', 4, stickyColors.map(x =>
        Item(id('stickiesBig', x), null, { color: x, large: true, blend: .75 }, ItemPreview(StickyPreview))
    ))
]);

const indexCards = ItemGroup('indexCards', 'Index Cards', 'deqo://icons/d2/index-card', [
    ItemSet('indexCards', 'Index Cards', 2, brandColors.map(x =>
        Item(id('indexCards', x), null, { color: x }, ItemPreview(IndexCardPreview))
    ))
]);

const taskCards = ItemGroup('taskCards', 'Task Cards', 'deqo://icons/d2/task-card', [
    ItemSet('taskCards', 'Task Cards', 3, stickyColors.slice(0, -1).map(x =>
        Item(id('taskCards', x), null, { color: x }, ItemPreview(TaskCardPreview))
    ))
]);

const roadmapCardColors = ColorGroups[3].slice(0, 19).concat(['#ffffff']);

const roadmapCards = ItemGroup('roadmapCards', 'Roadmap Cards', 'deqo://icons/d2/roadmap-card', [
    ItemSet('roadmapCards', 'Roadmap Cards', 2, roadmapCardColors.slice(0, -1).map(x =>
        Item(id('roadmapCards', x), null, { color: x }, ItemPreview(RoadmapCardPreview))
    ))
]);

const createModel = (tools: ToolManager) => [
    ...(tools.isEnabled('stickyTool') ? [stickies] : []),
    ...(tools.isEnabled('indexCardTool') ? [indexCards] : []),
    ...(tools.isEnabled('taskCardTool') ? [taskCards] : []),
    ...(tools.isEnabled('roadmapCardTool') ? [roadmapCards] : []),
];

export const PaperWidget = React.memo<{ widget: PopoutWidget }>(({ widget }) => {

    const dispatch = useResolve(CommandDispatcher);
    const tools = useObserverReturningStore(useResolve(ToolManager));

    const onSelect = useCallback((item: any, selectMode: 'click' | 'drag') => {
        widget.update({ open: !widget.autoClose });

        const toolId =
            (item.id.startsWith('stickies') ? 'stickyTool' : null) ??
            (item.id.startsWith('indexCards') ? 'indexCardTool' : null) ??
            (item.id.startsWith('taskCards') ? 'taskCardTool' : null) ??
            (item.id.startsWith('roadmapCards') ? 'roadmapCardTool' : null) ??
            'default';

        const mode = selectMode === 'click' ? 'plop' : 'drop';
        const opt = !item.props.value ? 'edit' : '';

        tools.setSettings(toolId, item.props);
        dispatch(toolEquip, [toolId, `${mode}:${opt}`]);

    }, []);

    const model = createModel(tools);

    return <Prompt ignoreMouse ignoreEnter onAction={() => widget.update({ open: false })}>
        <Flexer relative>
            <ElementSelector name="paper" model={model} onSelectItem={onSelect} />
            <Position inset=".6" at="topRight">
                <KeepPopoutWidgetOpenButton widget={widget} />
            </Position>
            {/* <HaugButtonHangingMount>
                <HaugButton label="Learn about Stickies & Cards" haug={PaperHaug} />
            </HaugButtonHangingMount> */}
        </Flexer>
    </Prompt>;
});


